import dayjs from 'dayjs';
import {
  MaterialReactTable,
  MRT_ActionMenuItem,
  MRT_ColumnDef,
  useMaterialReactTable,
} from 'material-react-table';
import { FC, useMemo } from 'react';
import DisableIcon from '@/assets/disable.svg';
import ConfirmBreakConnectionModal, {
  BREAK_CONNECTION_CACHE_KEY,
} from '@/components/modals/confirm-break-connection';
import { InfoBar } from '@/components/ui/info-bar';
import { SectionHeadingGroup } from '@/components/ui/section-heading-group';
import { StatusBadge } from '@/components/ui/status-badge';
import { useCurrentUnit } from '@/hooks/use-current-unit';
import { useUnitFfConnections } from '@/hooks/use-ff-connections';
import { useOpenState } from '@/hooks/use-open-state';
import { api } from '@/redux/api';
import { composeTableOptions } from '@/table';
import { FfConnection } from '@/types/ff-connection';

const formatDate = (value: string | null): string =>
  value ? dayjs(value).format('DD/MM/YYYY') : '—';

const columns: MRT_ColumnDef<FfConnection>[] = [
  {
    accessorKey: 'relative',
    header: 'Relative',
    grow: true,
    size: 150,
    Cell: ({ row }) => row.original.relative ?? 'Not set',
  },
  {
    accessorKey: 'pairedAt',
    header: 'Paired',
    size: 130,
    Cell: ({ row }) => formatDate(row.original.pairedAt),
  },
  {
    accessorKey: 'expiryTime',
    header: 'Expires',
    size: 130,
    Cell: ({ row }) => formatDate(row.original.expiryTime),
  },
  {
    accessorKey: 'isExpired',
    header: 'Status',
    size: 120,
    Cell: ({ row }) =>
      row.original.isExpired ? (
        <StatusBadge text="Expired" variant="warning" />
      ) : (
        <StatusBadge text="Active" variant="success" />
      ),
  },
];

type BreakConnectionContext = {
  connectionId: number;
  relative: string | null;
};

export const FfConnectionsView: FC = () => {
  const { data: connections, isLoading, isError } = useUnitFfConnections();
  const { data: unit } = useCurrentUnit();
  const unitId = unit?.id ?? -1;

  // Observe the in-flight break triggered from the modal so we can mark the
  // affected row as pending while it resolves and the list refetches.
  const [, { isLoading: isBreaking, originalArgs: breakingArgs }] =
    api.useDeleteUnitFfConnectionMutation({
      fixedCacheKey: BREAK_CONNECTION_CACHE_KEY,
    });
  const breakingConnectionId = isBreaking ? breakingArgs?.connectionId : null;

  const {
    state: breakConfirmationState,
    open: openBreakConfirmation,
    toggleOpen: toggleBreakConfirmationOpen,
  } = useOpenState<BreakConnectionContext>({
    connectionId: -1,
    relative: null,
  });

  const table = useMaterialReactTable({
    ...composeTableOptions<FfConnection>({ enableRowActions: true }),
    columns,
    enableColumnResizing: true,
    data: useMemo(() => connections, [connections]),
    state: {
      isLoading,
      ...(isLoading ? { pagination: { pageIndex: 0, pageSize: 1 } } : {}),
    },
    muiTableBodyRowProps: ({ row }) => ({
      hover: false,
      sx:
        row.original.id === breakingConnectionId
          ? { opacity: 0.5, pointerEvents: 'none' }
          : undefined,
    }),
    renderRowActionMenuItems: ({ row, table, closeMenu }) => [
      <MRT_ActionMenuItem
        icon={<DisableIcon />}
        key="break"
        label="Break connection"
        onClick={() => {
          closeMenu();
          openBreakConfirmation({
            connectionId: row.original.id,
            relative: row.original.relative,
          });
        }}
        table={table}
      />,
    ],
  });

  return (
    <>
      <div>
        <SectionHeadingGroup
          className="mb-6"
          subtitle="Manage the Friends & Family app connections paired to this unit."
          title="Friends & Family connections"
        />
        {isError ? (
          <InfoBar
            className="mb-4"
            text="Couldn't load Friends & Family connections for this unit. Please try again."
            variant="error"
          />
        ) : null}
        <MaterialReactTable table={table} />
      </div>
      <ConfirmBreakConnectionModal
        connectionId={breakConfirmationState.context.connectionId}
        isOpen={breakConfirmationState.open}
        onCancel={() => toggleBreakConfirmationOpen(false)}
        onSend={() => toggleBreakConfirmationOpen(false)}
        relative={breakConfirmationState.context.relative}
        unitId={unitId}
      />
    </>
  );
};
